/**
 * Gap board display helpers — labels, ordering and formatting.
 * Pure functions only; no React, no data access.
 */

import type {
  Gap,
  GapAction,
  GapActionType,
  GapActionUser,
  GapDetail,
  GapFixStatus,
  GapFixTargetRef,
  GapFixTargetType,
  GapPriorityBand,
  GapRunCaseKind,
  GapRunRow,
  GapRunRowStatus,
  GapRunSet,
  GapRunSetStatus,
  GapRunVerdict,
  GapSeverityLevel,
  GapStatus,
  GapSuggestedFix,
  GapTicketStatus,
  GapTraceRef,
  LadderRung,
  GapThreadBlueprint,
  GapThreadFeedback,
  GapThreadRefine,
  GapThreadResponse,
  GapThreadTicket,
  GapThreadTurn,
  GapWorldPatchStatus,
} from './types';
import { getIntegrationName } from '@/modules/integrations/constants';

/** Walk order of the causal ladder. The investigator stops at the first broken rung. */
export const RUNG_ORDER: readonly LadderRung[] = [
  'comprehension',
  'knowledge_availability',
  'retrieval_execution',
  'instruction',
  'reasoning',
  'authority',
  'delivery',
] as const;

const RUNG_LABELS: Record<LadderRung, string> = {
  comprehension: 'Comprehension',
  knowledge_availability: 'Knowledge availability',
  retrieval_execution: 'Retrieval execution',
  instruction: 'Instruction',
  reasoning: 'Reasoning',
  authority: 'Authority',
  delivery: 'Delivery',
};

/**
 * Every status a gap can hold. `superseded` is deliberately absent from the
 * board's tabs — it is a store-internal outcome of a merge, not a lane a human
 * moves work into — but is still a valid value to render.
 */
export const GAP_STATUSES: readonly GapStatus[] = [
  'open',
  'routed_to_tech',
  'test_passed',
  'shipped',
  'ignored',
  'superseded',
] as const;

/** The status tabs, in board order. */
export const GAP_STATUS_TABS: readonly GapStatus[] = [
  'open',
  'routed_to_tech',
  'test_passed',
  'shipped',
  'ignored',
] as const;

const STATUS_LABELS: Record<GapStatus, string> = {
  open: 'Open',
  routed_to_tech: 'Routed to tech',
  test_passed: 'Test Passed',
  shipped: 'Shipped',
  ignored: 'Ignored',
  superseded: 'Superseded',
};

export const GAP_PRIORITY_BANDS: readonly GapPriorityBand[] = ['P1', 'P2', 'P3', 'P4'] as const;

export const GAP_SEVERITY_LEVELS: readonly GapSeverityLevel[] = [
  'weak',
  'normal',
  'critical',
] as const;

const SEVERITY_LABELS: Record<GapSeverityLevel, string> = {
  weak: 'Weak',
  normal: 'Normal',
  critical: 'Critical',
};

const ACTION_LABELS: Record<GapActionType, string> = {
  ack: 'Acknowledged',
  // The board calls this action "Ignore Gap" and it lands the gap in Ignored, so its label
  // says Ignored — "Disputed" is the wire name, never what an operator clicked.
  dispute: 'Ignored',
  mark_fixed: 'Marked shipped',
  note: 'Note',
  route_tech: 'Routed to tech',
  // `unroute_tech` now only undoes a tech route, but rows written before
  // `reopen` existed also used it for a pass or a dismissal taken back, so the
  // label names the effect, which is true for every one of them.
  unroute_tech: 'Reopened',
  reopen: 'Reopened',
  fix_resolve: 'Marked resolved',
  fix_ignore: 'Ignored',
  fix_route_tech: 'Routed to tech',
  fix_reopen: 'Reset to open',
  fix_patch_approve: 'Approved data correction',
  fix_patch_reject: 'Rejected data correction',
  test_passed: 'Test passed',
  test_failed: 'Test failed',
  pass_revoked: 'Pass revoked',
};

/** Turns any unrecognised snake_case or camelCase wire token into prose. */
export function humanizeToken(token: string): string {
  const spaced = token
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return '—';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function formatRungLabel(rung: LadderRung | null): string {
  return rung ? RUNG_LABELS[rung] : '—';
}

export function rungPosition(rung: LadderRung | null): number | null {
  if (!rung) return null;
  return RUNG_ORDER.indexOf(rung) + 1;
}

export function formatStatusLabel(status: GapStatus | null, rawStatus: string): string {
  return status ? STATUS_LABELS[status] : humanizeToken(rawStatus);
}

export function formatSeverityLabel(level: GapSeverityLevel | null, rawLevel: string | null): string {
  if (level) return SEVERITY_LABELS[level];
  return rawLevel ? humanizeToken(rawLevel) : '—';
}

export function formatActionLabel(type: GapActionType | null, rawType: string): string {
  return type ? ACTION_LABELS[type] : humanizeToken(rawType);
}

/** Impact share is a raw percentage; one decimal is enough to keep 11.4 and 11.6 apart. */
export function formatImpactShare(impactShare: number | null): string {
  if (impactShare === null) return '—';
  return `${Number(impactShare.toFixed(1))}%`;
}

export function formatPriorityScore(score: number | null): string {
  if (score === null) return '—';
  return String(Math.round(score));
}

/**
 * The impacted count with its unit, labelled "Ticket(s)" to match the Figma
 * column header (list frame node 4848:8453).
 *
 * The wire field behind it is `impactedTickets` — it is the backend's name
 * and the API's sort key — and it counts distinct impacted tickets
 * (conversations), matching this label.
 */
export function formatImpactedTickets(count: number): string {
  return `${count} Ticket${count === 1 ? '' : 's'}`;
}

/** What `formatTicketId`/`ticketDisplayId` need from a ticket — just its three candidate ids. */
type TicketIdCandidate = Pick<GapThreadTicket, 'conversationId' | 'externalId' | 'displayId'>;

/**
 * The id a human should read for a ticket: the helpdesk's own id, else Kim's ticket number,
 * else Kim's internal conversation id — the fallback every ticket has, and the id the board
 * showed before the helpdesk/Kim ids existed. `GapThreadTicket.sentinelTicketId` (Sentinel's
 * own ticket number) is a separate internal id and is never shown, on any of these fallbacks.
 */
export function ticketDisplayId(ticket: TicketIdCandidate): string {
  return ticket.externalId ?? ticket.displayId ?? String(ticket.conversationId);
}

/** The `# <id>` label for a ticket list row or pill — see `ticketDisplayId` for which id wins. */
export function formatTicketId(ticket: TicketIdCandidate): string {
  return `# ${ticketDisplayId(ticket)}`;
}

/** The turn the ticket drawer opens on: the one the gap was found on, else the latest. */
export function openingTurnIndex(turns: readonly Pick<GapThreadTurn, 'isGapTurn'>[]): number {
  const gapTurnIndex = turns.findIndex((turn) => turn.isGapTurn);
  return gapTurnIndex >= 0 ? gapTurnIndex : turns.length - 1;
}

/** The newest feedback in a set — the drawer's feedback block. */
export function latestFeedback(feedbacks: readonly GapThreadFeedback[]): GapThreadFeedback | null {
  let latest: GapThreadFeedback | null = null;
  for (const feedback of feedbacks) {
    if (!latest || Date.parse(feedback.createdAt) > Date.parse(latest.createdAt)) latest = feedback;
  }
  return latest;
}

/** The feedback block's title: a reviewer's "no" reads as a rejection. */
export function feedbackTitle(feedback: Pick<GapThreadFeedback, 'verdict'> | null): string {
  return feedback?.verdict === 'no' ? 'Rejection Feedback' : 'Resolution Feedback';
}

/** What a reviewer asked for in a refine: their note, else the tone they picked. */
export function refineText(refine: Pick<GapThreadRefine, 'note' | 'command'>): string | null {
  if (refine.note?.trim()) return refine.note.trim();
  return refine.command?.trim() ? `Make it ${refine.command.trim()}` : null;
}

/** The chip on a Sentinel Flow card. */
export type GapFlowChip = 'used' | 'refined' | 'replanned' | 'rejected';

/** A blueprint revision: the last one was used; an earlier one was refined by a reviewer or re-planned by Sentinel. */
export function blueprintChip(blueprints: readonly Pick<GapThreadBlueprint, 'cause'>[], index: number): GapFlowChip {
  const next = blueprints[index + 1];
  if (!next) return 'used';
  return next.cause === 'auto_replan' ? 'replanned' : 'refined';
}

/** A reviewer's "no" left on this response, if any. */
export function responseRejection(
  turn: Pick<GapThreadTurn, 'feedbacks'>,
  response: Pick<GapThreadResponse, 'aiEventLogId'>
): GapThreadFeedback | null {
  return latestFeedback(
    turn.feedbacks.filter((feedback) => feedback.aiEventLogId === response.aiEventLogId && feedback.verdict === 'no')
  );
}

/**
 * A generated response: sent → used; a reviewer said no → rejected; replaced by
 * a later one → refined. The last one on a turn that ended unassigned was
 * rejected too. Null while it is still waiting on a reviewer.
 */
export function responseChip(
  turn: Pick<GapThreadTurn, 'responses' | 'feedbacks' | 'endState'>,
  index: number
): GapFlowChip | null {
  const response = turn.responses[index];
  if (!response) return null;
  if (response.sent) return 'used';
  if (responseRejection(turn, response)) return 'rejected';
  if (index < turn.responses.length - 1) return 'refined';
  return turn.endState === 'unassigned' ? 'rejected' : null;
}

/** A Sentinel Flow card's time, in the design's "9:24 PM" shape. */
export function formatTime(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** What the reviewer asked for, in their own words: their note, else their rewrite. */
export function feedbackText(feedback: Pick<GapThreadFeedback, 'otherText' | 'rewrite'>): string | null {
  return feedback.otherText?.trim() || feedback.rewrite?.trim() || null;
}

/** The id humans quote. Falls back to the Mongo id's tail on pre-board documents. */
export function formatDisplayId(displayId: number | null, id: string): string {
  return displayId === null ? id.slice(-7) : String(displayId);
}

export const GAP_TICKET_STATUSES: readonly GapTicketStatus[] = ['resolved', 'unresolved'] as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * The Tickets row's right-aligned age, in the design's "4D Ago" shape
 * (Figma node 4848:8801). Coarser than `formatDateTime` on purpose — the row
 * shows how stale a ticket is, not when it happened.
 *
 * A future timestamp reads as "Just now" rather than a negative age: clock
 * skew between the API and the browser is the only way to get one.
 */
export function formatRelativeAge(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const elapsedMs = now.getTime() - date.getTime();
  if (elapsedMs < MINUTE_MS) return 'Just now';
  if (elapsedMs < HOUR_MS) return `${Math.floor(elapsedMs / MINUTE_MS)}M Ago`;
  if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)}H Ago`;
  return `${Math.floor(elapsedMs / DAY_MS)}D Ago`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatSimilarity(similarity: number | null): string {
  if (similarity === null) return '—';
  return similarity.toFixed(3);
}

export function formatHoursAfter(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m after trace`;
  return `${Number(hours.toFixed(1))}h after trace`;
}

/**
 * A trace is "outcome-grounded" only when a human reply was actually recorded
 * against this draft. `groundTruth` is legitimately null on recent traces — the
 * board must never substitute a nearby signal for the missing one.
 */
export function hasOutcomeEvidence(traceRef: GapTraceRef): boolean {
  const groundTruth = traceRef.groundTruth;
  if (!groundTruth) return false;
  return Boolean(groundTruth.ourDraft) || Boolean(groundTruth.humanSent);
}


/**
 * True once the writer has recorded any part of the "Gap report" block
 * (expected/actual/cause chain). Gaps investigated before this field shipped
 * carry `expected: null`, `actual: null`, `causeChain: []` — that's the
 * common case, not a rare edge, so the block renders a "cause unknown"
 * notice rather than hiding itself when this is false.
 */
export function hasGapReportContent(
  gap: Pick<Gap, 'expected' | 'actual' | 'causeChain'>
): boolean {
  return Boolean(gap.expected || gap.actual || gap.causeChain.length > 0);
}

export const GAP_FIX_TARGET_TYPES: readonly GapFixTargetType[] = [
  'sop',
  'workflow',
  'persona',
  'kb',
  'test_set',
  'code',
  'tool',
  'integration',
  'configuration',
] as const;

const FIX_TARGET_TYPE_LABELS: Record<GapFixTargetType, string> = {
  sop: 'Protocol',
  workflow: 'Workflow',
  persona: 'Persona',
  kb: 'Knowledge base',
  test_set: 'Test set',
  code: 'Code',
  tool: 'Tool',
  integration: 'Integration',
  configuration: 'Setting',
};

/**
 * The name a fix's own ref gives its chip, for the types whose ref IS the name: a tool by its
 * tool name, an integration by its provider as the Integrations page names it, a setting by its
 * key. Null for a type whose ref holds an id, since an id is never shown.
 */
function nameFromTargetRef(targetRef: GapFixTargetRef | null): string | null {
  if (!targetRef) return null;
  if ('toolName' in targetRef) return targetRef.toolName;
  if ('settingKey' in targetRef) return targetRef.settingKey;
  if ('provider' in targetRef) return getIntegrationName(targetRef.provider) ?? targetRef.provider;
  return null;
}

/** What a Suggested Fixes chip reads: the fix's type, then the target's name when it has one. */
export interface FixTargetTag {
  typeLabel: string;
  name: string | null;
}

/**
 * The Suggested Fixes chip. The type is always there, so a tool and a workflow never look alike;
 * the name is the target's own, else the one its ref gives it — never a raw id. Null when
 * `targetType` is null, so the card omits the chip.
 */
export function fixTargetTag(
  targetType: GapFixTargetType | null,
  targetName: string | null,
  targetRef: GapFixTargetRef | null = null
): FixTargetTag | null {
  if (!targetType) return null;
  return {
    typeLabel: FIX_TARGET_TYPE_LABELS[targetType],
    name: targetName?.trim() || nameFromTargetRef(targetRef),
  };
}

/** Numbered-card order for the Suggested Fixes list — 1-based, as the design shows it. */
export function numberedFixes(
  fixes: readonly GapSuggestedFix[]
): { position: number; fix: GapSuggestedFix }[] {
  return fixes.map((fix, index) => ({ position: index + 1, fix }));
}

export const GAP_FIX_STATUSES: readonly GapFixStatus[] = [
  'open',
  'resolved',
  'ignored',
  'routed_to_tech',
] as const;

const FIX_STATUS_LABELS: Record<Exclude<GapFixStatus, 'open'>, string> = {
  resolved: 'Resolved',
  ignored: 'Ignored',
  routed_to_tech: 'Routed to tech',
};

/**
 * The fix card's state chip label. Null for `open`/null — the design shows
 * no chip at all for a fix nothing has happened to yet.
 */
export function formatFixStatusLabel(status: GapFixStatus | null): string | null {
  if (!status || status === 'open') return null;
  return FIX_STATUS_LABELS[status];
}

/**
 * Suggested fixes still blocking a resolve: `open`, or no status yet.
 *
 * The client's copy of the rule. Prefer `resolveUnaddressedFixCount`, which
 * uses the server's number when it sent one — this stays only as the fallback
 * for a response that predates the field.
 */
export function unaddressedFixCount(fixes: readonly GapSuggestedFix[]): number {
  return fixes.filter((fix) => fix.status === null || fix.status === 'open').length;
}

/**
 * The count that gates a resolve. The server's number wins: it is the same one
 * the backend's 400 on an early `mark_fixed` is based on, so preferring it
 * stops the confirmation dialog and the API from disagreeing about whether a
 * gap can be resolved at all.
 *
 * `serverCount` is null on a response that carried no count. Falling back to
 * the client rule is deliberate — the alternative, treating "absent" as zero,
 * would silently open the resolve dialog on a gap with fixes still open.
 */
export function resolveUnaddressedFixCount(
  serverCount: number | null,
  fixes: readonly GapSuggestedFix[]
): number {
  const clientCount = unaddressedFixCount(fixes);
  if (serverCount === null) return clientCount;

  if (serverCount !== clientCount) {
    // The drift this function exists to remove. The server still wins; the log
    // is how we find out the two rules have parted company.
    console.warn('[gaps] unaddressed-fix count disagrees with the server', {
      serverCount,
      clientCount,
      fixStatuses: fixes.map((fix) => fix.status),
    });
  }

  return serverCount;
}

/** A fix-level action, as the fix card submits it. */
export type FixActionType = 'fix_resolve' | 'fix_ignore' | 'fix_route_tech' | 'fix_reopen';

export interface FixMenuAction {
  type: FixActionType;
  label: string;
}

/** Every move a fix can make, with the status each one lands on. */
const FIX_MOVES: readonly (FixMenuAction & { toStatus: GapFixStatus })[] = [
  { type: 'fix_reopen', label: 'Reset to open', toStatus: 'open' },
  { type: 'fix_resolve', label: 'Mark as resolved', toStatus: 'resolved' },
  { type: 'fix_ignore', label: 'Ignore fix', toStatus: 'ignored' },
  { type: 'fix_route_tech', label: 'Route fix to tech', toStatus: 'routed_to_tech' },
];

/** Whether a fix still needs acting on — `open`, or no status recorded yet. */
export function isFixOpen(status: GapFixStatus | null): boolean {
  return status === null || status === 'open';
}

/**
 * The fix card's ⋮ menu: every move except to the status the fix already has. An open fix's
 * resolve and ignore sit on the card's own buttons, so its menu offers only routing.
 */
export function fixMenuActions(status: GapFixStatus | null): readonly FixMenuAction[] {
  const current: GapFixStatus = status ?? 'open';
  const onButtons: readonly FixActionType[] = isFixOpen(status) ? ['fix_resolve', 'fix_ignore'] : [];
  return FIX_MOVES.filter((move) => move.toStatus !== current && !onButtons.includes(move.type)).map(
    ({ type, label }) => ({ type, label }),
  );
}

/** A shipped or superseded gap is closed — its fixes are a record, no longer acted on. */
export function areFixActionsLocked(gapStatus: GapStatus | null): boolean {
  return gapStatus === 'shipped' || gapStatus === 'superseded';
}

/** The per-fix action types — each REQUIRES a `fixId`; the backend rejects one without a valid fixId. */
export const FIX_ACTION_TYPES: readonly GapActionType[] = [
  'fix_resolve',
  'fix_ignore',
  'fix_route_tech',
  'fix_reopen',
  'fix_patch_approve',
  'fix_patch_reject',
] as const;

/** Action types that require a free-text comment. */
export const COMMENT_REQUIRED_ACTIONS: readonly GapActionType[] = ['dispute', 'note'] as const;

export function requiresComment(type: GapActionType): boolean {
  return COMMENT_REQUIRED_ACTIONS.includes(type);
}

export const ACTION_COMMENT_MAX_LENGTH = 1000;

// ---------------------------------------------------------------------------
// M3 Phase 1 status machine — gap-level board action gating
// ---------------------------------------------------------------------------

/**
 * The gap-level board actions `GapActionBar` renders. `fix_*` types are
 * per-fix (gated by `fixId`, not gap status) and never appear here.
 */
export type GapBoardActionType = Exclude<
  GapActionType,
  'fix_resolve' | 'fix_ignore' | 'fix_route_tech' | 'fix_reopen' | 'fix_patch_approve' | 'fix_patch_reject'
>;

/** Record history, never a status — legal in every status, known or not. */
const ALWAYS_LEGAL_GAP_ACTIONS: readonly GapBoardActionType[] = ['ack', 'note'];

/**
 * The status machine from M3_PLAN.md Phase 1, as a table rather than a
 * condition per button — a re-added edge (e.g. `open → shipped`) fails by
 * simply not being in the list, not by someone remembering to guard a button.
 *
 * `mark_fixed` only from `test_passed` — the PRD's only road to Shipped.
 * `dispute` only from `open` — once routed or passed, disputing the gap's
 * validity no longer applies. `unroute_tech` returns `routed_to_tech` to
 * `open`; `reopen` returns `test_passed` or `ignored` to `open`. The backend
 * refuses either from any other status.
 *
 * `superseded` is a store-internal merge outcome, not a lane a human acts
 * from (see `GAP_STATUSES`) — it gets no status-specific actions, same as
 * the terminal `shipped`.
 */
const STATUS_SPECIFIC_GAP_ACTIONS: Record<GapStatus, readonly GapBoardActionType[]> = {
  open: ['route_tech', 'dispute'],
  test_passed: ['mark_fixed', 'route_tech', 'reopen'],
  routed_to_tech: ['unroute_tech'],
  shipped: [],
  ignored: ['reopen'],
  superseded: [],
};

/**
 * The legal gap-level board actions for a status. A missing status (`null`)
 * is treated as `open`, mirroring the backend's `gap.status ?? 'open'`
 * (`gap.service.ts`) — a gap with no recognised status is still actionable
 * via the API on that basis, so the UI must not be stricter than the server.
 */
export function legalGapActions(status: GapStatus | null): readonly GapBoardActionType[] {
  const statusActions = STATUS_SPECIFIC_GAP_ACTIONS[status ?? 'open'];
  return [...ALWAYS_LEGAL_GAP_ACTIONS, ...statusActions];
}

export function isGapActionLegal(status: GapStatus | null, type: GapBoardActionType): boolean {
  return legalGapActions(status).includes(type);
}

/**
 * The statuses an action is legal from, in board order. Derived from the same
 * table `legalGapActions` reads rather than restated, so an edge added to or
 * removed from the machine can never leave a stale "only from …" sentence
 * behind.
 */
export function statusesAllowingGapAction(type: GapBoardActionType): readonly GapStatus[] {
  return GAP_STATUSES.filter((status) => isGapActionLegal(status, type));
}

/** "Open", "Open or Test Passed", "Open, Test Passed or Ignored". */
function formatStatusList(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

/**
 * Why an action is unavailable from the gap's current status, naming the
 * statuses it *is* available from — an operator who can see the button needs
 * to know where the move lives, not just that it's off here.
 *
 * One function for both surfaces (the board row menu and the detail action
 * bar) so the same rule never reads two different ways.
 */
export function gapActionBlockedReason(
  type: GapBoardActionType,
  status: GapStatus | null,
  rawStatus: string
): string {
  const currentLabel = formatStatusLabel(status, rawStatus);
  const allowedLabels = statusesAllowingGapAction(type).map((allowed) =>
    formatStatusLabel(allowed, allowed)
  );
  if (allowedLabels.length === 0) return `Not available while the gap is ${currentLabel}`;
  return `Not available while the gap is ${currentLabel} — only from ${formatStatusList(allowedLabels)}`;
}

/**
 * The action behind "Reset to open" for a status: `unroute_tech` undoes a tech
 * route; `reopen` takes back a pass or a dismissal. Any other status gets
 * `reopen`, which `isGapActionLegal` then refuses.
 */
export function resetToOpenAction(status: GapStatus | null): 'unroute_tech' | 'reopen' {
  return status === 'routed_to_tech' ? 'unroute_tech' : 'reopen';
}

// ---------------------------------------------------------------------------
// Testing rail — gap test runs
// ---------------------------------------------------------------------------

export const GAP_RUN_SET_STATUSES: readonly GapRunSetStatus[] = [
  'running',
  'passed',
  'failed',
  'cancelled',
  'stale',
] as const;

export const GAP_RUN_ROW_STATUSES: readonly GapRunRowStatus[] = [
  'pending',
  'running',
  'done',
  'skipped',
  'error',
] as const;

export const GAP_RUN_VERDICTS: readonly GapRunVerdict[] = [
  'pass',
  'pass_with_flag',
  'fail',
  'unchanged_or_better',
  'regressed',
] as const;

export const GAP_RUN_CASE_KINDS: readonly GapRunCaseKind[] = ['failing_ticket', 'regression'] as const;

/** A run set's terminal statuses — the rail stops polling and shows a Re-run button. */
export const TERMINAL_RUN_SET_STATUSES: readonly GapRunSetStatus[] = [
  'passed',
  'failed',
  'cancelled',
  'stale',
] as const;

const RUN_SET_STATUS_LABELS: Record<GapRunSetStatus, string> = {
  running: 'running pipeline',
  passed: 'Passed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  stale: 'Timed out',
};

export function formatRunSetStatusLabel(status: GapRunSetStatus | null): string {
  return status ? RUN_SET_STATUS_LABELS[status] : 'Unknown';
}

// ---------------------------------------------------------------------------
// Run history (Testing rail)
// ---------------------------------------------------------------------------

/**
 * Whether a `stale` run set is a pass that Resolve took back because a fix target changed
 * (backend `revokeDriftedPass`), rather than a run the stalled-run sweep timed out.
 *
 * Both write `stale` and the wire carries no reason, so this reads the totals: a revoked pass
 * finished every planned ticket with none failed and no regression — a timed-out run, by
 * definition, stopped before finishing them.
 */
export function isRevokedPass(run: Pick<GapRunSet, 'status' | 'totals'>): boolean {
  const { totals } = run;
  return (
    run.status === 'stale' &&
    totals.planned > 0 &&
    totals.finished === totals.planned &&
    totals.failed === 0 &&
    totals.regressions === 0
  );
}

/** The history row's pill label — the run-set status label, except a revoked pass reads "Invalidated". */
export function formatRunHistoryStatusLabel(run: Pick<GapRunSet, 'status' | 'totals'>): string {
  return isRevokedPass(run) ? 'Invalidated' : formatRunSetStatusLabel(run.status);
}

/**
 * The history row's score line: "2/3 passed", "1/3 finished" for a run that never got through
 * its tickets (running, cancelled, timed out), plus the regression count when there is one.
 */
export function formatRunHistoryScore(run: Pick<GapRunSet, 'status' | 'totals'>): string {
  const { totals } = run;
  const judgedEveryTicket = run.status === 'passed' || run.status === 'failed' || isRevokedPass(run);
  const score = judgedEveryTicket
    ? `${totals.passed}/${totals.planned} passed`
    : `${totals.finished}/${totals.planned} finished`;
  if (totals.regressions <= 0) return score;
  return `${score} · ${totals.regressions} ${totals.regressions === 1 ? 'regression' : 'regressions'}`;
}

/** Who started a run: name, else email, else a neutral fallback for rows from before the field. */
export function formatRunStartedBy(run: Pick<GapRunSet, 'startedBy'>): string {
  return run.startedBy.name || run.startedBy.email || 'Unknown';
}

/** Whether a (possibly unvalidated, wire-string) run-set status is a terminal one. */
export function isTerminalRunSetStatus(status: string): boolean {
  return (TERMINAL_RUN_SET_STATUSES as readonly string[]).includes(status);
}

const RUN_ROW_STATUS_LABELS: Record<GapRunRowStatus, string> = {
  pending: 'Queued',
  running: 'Running',
  done: 'Done',
  skipped: 'Skipped',
  error: 'Error',
};

export function formatRunRowStatusLabel(status: GapRunRowStatus | null): string {
  return status ? RUN_ROW_STATUS_LABELS[status] : 'Unknown';
}

const RUN_VERDICT_LABELS: Record<GapRunVerdict, string> = {
  pass: 'Pass',
  pass_with_flag: 'Pass with flag',
  fail: 'Fail',
  unchanged_or_better: 'Unchanged or better',
  regressed: 'Regressed',
};

export function formatRunVerdictLabel(verdict: GapRunVerdict | null): string | null {
  return verdict ? RUN_VERDICT_LABELS[verdict] : null;
}

/** The Tickets section's row label: the conversation, else a short trace ref id. */
export function formatRunRowTicketLabel(row: Pick<GapRunRow, 'conversationId' | 'traceRefId'>): string {
  return row.conversationId !== null ? `#${row.conversationId}` : row.traceRefId.slice(-7);
}

/** The ticket result dialog's title — same id as the row label, spaced out per the approved mockup. */
export function formatRunRowDialogTitle(row: Pick<GapRunRow, 'conversationId' | 'traceRefId'>): string {
  const id = row.conversationId !== null ? String(row.conversationId) : row.traceRefId.slice(-7);
  return `# ${id} · Test result`;
}

/** A run set's newest activity is more than this old — the resume fallback kicks in. */
const RUN_SET_STOPPED_AFTER_MS = 15 * MINUTE_MS;

/** True once a `running` run set's last progress is stale — the backend's resume attempt failed. */
export function isRunSetStopped(updatedAt: string | null, now: Date = new Date()): boolean {
  if (!updatedAt) return false;
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return false;
  return now.getTime() - date.getTime() > RUN_SET_STOPPED_AFTER_MS;
}

/** Minutes since a stopped run set's last progress, for the pill's "no progress for N min" copy. */
export function minutesSinceRunSetUpdate(updatedAt: string | null, now: Date = new Date()): number {
  if (!updatedAt) return 0;
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return 0;
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / MINUTE_MS));
}

/** The ticket result dialog's "Previous" sub-label, per where that answer came from. */
export function formatPreviousSourceLabel(source: 'served' | 'previous_run' | null): string | null {
  if (source === 'served') return 'what Kim sent';
  if (source === 'previous_run') return 'previous test run';
  return null;
}

/** A row's outcome is decided — no further judging happens for it. */
const TERMINAL_RUN_ROW_STATUSES: readonly GapRunRowStatus[] = ['done', 'skipped', 'error'];

/** The progress pill's live counts, derived from every row of the run set. */
export interface GapRunProgressCounts {
  finished: number;
  pending: number;
  running: number;
  total: number;
  percent: number;
}

/**
 * The progress pill's live counts, over EVERY row of the newest run — the gap's own
 * `failing_ticket` rows and the run's regression replays alike.
 *
 * `totals.planned`/`totals.finished` (the wire's own counters) count `failing_ticket`
 * rows only, so once those finish the pill would read "N finished · 0 pending" at 100%
 * while regression rows are still being replayed. Counting every row instead keeps the
 * pill honest for as long as the run set is actually `running`.
 */
export function gapRunProgressFromRows(rows: readonly Pick<GapRunRow, 'status'>[]): GapRunProgressCounts {
  let finished = 0;
  let running = 0;
  for (const row of rows) {
    if (row.status && TERMINAL_RUN_ROW_STATUSES.includes(row.status)) finished++;
    else if (row.status === 'running') running++;
  }
  const total = rows.length;
  const pending = Math.max(total - finished, 0);
  const percent = total > 0 ? Math.round((finished / total) * 100) : 0;
  return { finished, pending, running, total, percent };
}

function isFinishedRunRow(row: Pick<GapRunRow, 'status'>): boolean {
  return row.status !== null && TERMINAL_RUN_ROW_STATUSES.includes(row.status);
}

function pluralizeRegressionChecks(count: number): string {
  return `${count} regression check${count === 1 ? '' : 's'}`;
}

/** The Testing header: "2 scenarios · 7 regression checks" — checks only once a run has planned some. */
export function formatTestingSubtitle(scenarioCount: number, regressionChecks: number): string | null {
  if (scenarioCount <= 0) return null;
  const scenarios = `${scenarioCount} scenario${scenarioCount === 1 ? '' : 's'}`;
  return regressionChecks > 0 ? `${scenarios} · ${pluralizeRegressionChecks(regressionChecks)}` : scenarios;
}

/**
 * What a running test run is busy with, under its progress pill. Rows run one at a time, the gap's
 * own tickets first and then the regression checks, so once the tickets finish the run is still
 * working — without this line it looks stuck. `null` when the run has no checks or nothing is left.
 */
export function describeRunningPhase(rows: readonly Pick<GapRunRow, 'caseKind' | 'status'>[]): string | null {
  const checks = rows.filter((row) => row.caseKind === 'regression');
  if (checks.length === 0) return null;
  const ticketsLeft = rows.some((row) => row.caseKind !== 'regression' && !isFinishedRunRow(row));
  if (ticketsLeft) return `Running this gap's tickets, then ${pluralizeRegressionChecks(checks.length)}`;
  const checksFinished = checks.filter(isFinishedRunRow).length;
  if (checksFinished >= checks.length) return null;
  return `Running regression checks (${checksFinished + 1} of ${checks.length})`;
}

// ---------------------------------------------------------------------------
// Tickets tab — each ticket's result in the gap's newest test run
// ---------------------------------------------------------------------------

/** What the newest test run says about one ticket. */
export type TicketTestResult = 'passed' | 'failed' | 'running' | 'not_tested';

const PASSING_VERDICTS: readonly GapRunVerdict[] = ['pass', 'pass_with_flag'];

function rowTestResult(row: GapRunRow): TicketTestResult {
  if (row.status === 'pending' || row.status === 'running') return 'running';
  if (row.status !== 'done' || !row.verdict) return 'not_tested';
  return PASSING_VERDICTS.includes(row.verdict) ? 'passed' : 'failed';
}

/**
 * One ticket can have several turns under test: any failure fails it, a row still in flight
 * keeps it running, and it passes only when every row passed.
 */
function combineRowResults(results: readonly TicketTestResult[]): TicketTestResult {
  if (results.includes('failed')) return 'failed';
  if (results.includes('running')) return 'running';
  if (results.length > 0 && results.every((result) => result === 'passed')) return 'passed';
  return 'not_tested';
}

/**
 * Each ticket's result in a run set, keyed by conversation id. Only `failing_ticket` rows count —
 * a regression row replays a ticket for a DIFFERENT gap and would misattribute its verdict here.
 * Rows with no ticket are left out.
 */
export function ticketTestResults(rows: readonly GapRunRow[]): Map<number, TicketTestResult> {
  const resultsByTicket = new Map<number, TicketTestResult[]>();
  for (const row of rows) {
    if (row.caseKind !== 'failing_ticket' || row.conversationId === null) continue;
    const results = resultsByTicket.get(row.conversationId) ?? [];
    results.push(rowTestResult(row));
    resultsByTicket.set(row.conversationId, results);
  }
  return new Map([...resultsByTicket].map(([conversationId, results]) => [conversationId, combineRowResults(results)]));
}

const TICKET_TEST_RESULT_LABELS: Record<TicketTestResult, string> = {
  passed: 'Passed',
  failed: 'Failed',
  running: 'Running',
  not_tested: 'Not tested',
};

export function formatTicketTestResult(result: TicketTestResult): string {
  return TICKET_TEST_RESULT_LABELS[result];
}

// ---------------------------------------------------------------------------
// Data correction (world patch) — a tool-data fix's corrected tool fields
// ---------------------------------------------------------------------------

const WORLD_PATCH_STATUS_LABELS: Record<GapWorldPatchStatus, string> = {
  proposed: 'Data correction proposed',
  approved: 'Data correction approved',
  rejected: 'Data correction rejected',
};

export function formatWorldPatchStatus(status: GapWorldPatchStatus): string {
  return WORLD_PATCH_STATUS_LABELS[status];
}

/** A corrected value as the operator should read it: strings quoted, everything else as JSON. */
export function formatWorldPatchValue(value: unknown): string {
  if (value === undefined) return 'null';
  return JSON.stringify(value) ?? 'null';
}

export type WorldPatchActionType = 'fix_patch_approve' | 'fix_patch_reject';

/** The decisions the dialog offers: both while proposed, else only the one that changes it. */
export function worldPatchActions(status: GapWorldPatchStatus): readonly WorldPatchActionType[] {
  if (status === 'approved') return ['fix_patch_reject'];
  if (status === 'rejected') return ['fix_patch_approve'];
  return ['fix_patch_reject', 'fix_patch_approve'];
}

// ---------------------------------------------------------------------------
// Activity tab — classification, verbs and the timeline itself
// ---------------------------------------------------------------------------

/**
 * kim-ai-backend's `GAP_ACTION_MAX_TEXT_LENGTH` (`src/constants/gaps.constants.ts`) — the limit
 * the controller enforces on every action's `text`, including a note. Kept as its own constant
 * (rather than reusing `ACTION_COMMENT_MAX_LENGTH`, which predates this and is unused) so the
 * Add-note counter never drifts from what the server actually accepts.
 */
export const GAP_NOTE_MAX_LENGTH = 2000;

/**
 * The action kind an activity row actually renders as: the validated `type` when the adapter
 * recognised it, else the raw wire string. Every Activity-tab helper reads a row this way rather
 * than `action.type` alone, so a row the adapter couldn't validate (a future action kind, or a
 * `type: null` row from a stale cache) still classifies correctly instead of falling through to
 * "unknown".
 */
export function gapActivityActionKind(action: Pick<GapAction, 'type' | 'rawType'>): string {
  return action.type ?? action.rawType;
}

/**
 * The exact text kim-ai-backend's `verdictActionText` (`gap.service.ts`) writes for a finished
 * verification run — `test_passed`, `test_failed` AND the `note` outcome all carry this same
 * shape, so the parser is shared rather than gated on the action's kind.
 */
const VERIFICATION_RUN_NOTE_PATTERN = /^Verification run (.+): passed (\d+), failed (\d+), skipped (\d+)$/;

export interface VerificationRunNoteCounts {
  runSetId: string;
  passed: number;
  failed: number;
  skipped: number;
}

/** Parses a verification run's counts out of its action text; `null` when the text doesn't match. */
export function parseVerificationRunNote(text: string | null): VerificationRunNoteCounts | null {
  if (!text) return null;
  const match = VERIFICATION_RUN_NOTE_PATTERN.exec(text);
  if (!match) return null;
  return {
    runSetId: match[1]!,
    passed: Number(match[2]),
    failed: Number(match[3]),
    skipped: Number(match[4]),
  };
}

/** kim-ai-backend's `runStartedActionText`: a run that just started, and what it will check. */
const RUN_STARTED_NOTE_PATTERN = /^Verification run (.+): started, (\d+) tickets, (\d+) checks$/;

/** kim-ai-backend's `runCancelledActionText`: how far a cancelled run got, then its own tickets' counts. */
const RUN_CANCELLED_NOTE_PATTERN =
  /^Verification run (.+): cancelled after (\d+) of (\d+), passed (\d+), failed (\d+), skipped (\d+)$/;

export interface RunStartedNote {
  runSetId: string;
  /** The gap's own tickets on the run. */
  tickets: number;
  /** Regression checks on the run. */
  checks: number;
}

export interface RunCancelledNote {
  runSetId: string;
  /** Rows that finished before the cancel, out of every row the run planned. */
  done: number;
  total: number;
  passed: number;
  failed: number;
  skipped: number;
}

/** Parses a run's start out of its action text; `null` when the text doesn't match. */
export function parseRunStartedNote(text: string | null): RunStartedNote | null {
  if (!text) return null;
  const match = RUN_STARTED_NOTE_PATTERN.exec(text);
  if (!match) return null;
  return { runSetId: match[1]!, tickets: Number(match[2]), checks: Number(match[3]) };
}

/** Parses a run's cancel out of its action text; `null` when the text doesn't match. */
export function parseRunCancelledNote(text: string | null): RunCancelledNote | null {
  if (!text) return null;
  const match = RUN_CANCELLED_NOTE_PATTERN.exec(text);
  if (!match) return null;
  return {
    runSetId: match[1]!,
    done: Number(match[2]),
    total: Number(match[3]),
    passed: Number(match[4]),
    failed: Number(match[5]),
    skipped: Number(match[6]),
  };
}

/**
 * A `note` action is a system test-run entry, not a person's note, when its text is one of the
 * backend's run texts above: a run's start, its cancel, or its finished counts.
 */
export function isVerificationRunNote(text: string | null): boolean {
  return (
    parseVerificationRunNote(text) !== null || parseRunStartedNote(text) !== null || parseRunCancelledNote(text) !== null
  );
}

/**
 * True for the three system-only kinds (see `GapActionType`) and for a `note` that is actually a
 * verification run's record — every other action is a person's.
 */
export function isSystemGapActivityAction(action: Pick<GapAction, 'type' | 'rawType' | 'text'>): boolean {
  const kind = gapActivityActionKind(action);
  if (kind === 'test_passed' || kind === 'test_failed' || kind === 'pass_revoked') return true;
  return kind === 'note' && isVerificationRunNote(action.text);
}

/**
 * The gap-level status an action moves the gap to, mirroring kim-ai-backend's
 * `GAP_LEVEL_ACTION_STATUS` (`gap.service.ts`) plus `pass_revoked`, which that map omits only
 * because the backend applies it through a different code path (`revokeDriftedPass`) — the effect
 * is the same "back to open" every other reopen kind has. `null` for every action that only
 * records history (`ack`, `note`, and every `fix_*`, which move a FIX's status, not the gap's).
 */
const GAP_LEVEL_ACTION_STATUS: Readonly<Record<string, GapStatus>> = {
  mark_fixed: 'shipped',
  dispute: 'ignored',
  route_tech: 'routed_to_tech',
  unroute_tech: 'open',
  reopen: 'open',
  test_passed: 'test_passed',
  test_failed: 'open',
  pass_revoked: 'open',
};

/** The Activity tab's status badge, or none for an action that doesn't move the gap. */
export function gapActivityResultingStatus(action: Pick<GapAction, 'type' | 'rawType'>): GapStatus | null {
  return GAP_LEVEL_ACTION_STATUS[gapActivityActionKind(action)] ?? null;
}

/** The filter chip a `fix_*` action always falls under. */
const FIX_ACTION_KIND_SET: ReadonlySet<string> = new Set(FIX_ACTION_TYPES);

export type GapActivityFilterGroup = 'status' | 'fixes' | 'test_runs' | 'notes';

/**
 * Which of the four filter chips an action belongs to. `pass_revoked` is a system entry but
 * still a status move, so it lives under Status, same as the person-written status actions
 * (per the approved spec). A verification run's `note` is a Test run, not a Note — everything
 * else falls to Status by default, which covers `ack`, `dispute`, `mark_fixed`, `route_tech`,
 * `unroute_tech` and `reopen`.
 */
export function gapActivityFilterGroup(
  action: Pick<GapAction, 'type' | 'rawType' | 'text'>
): GapActivityFilterGroup {
  const kind = gapActivityActionKind(action);
  if (kind === 'note') return isVerificationRunNote(action.text) ? 'test_runs' : 'notes';
  if (kind === 'test_passed' || kind === 'test_failed') return 'test_runs';
  if (kind === 'pass_revoked') return 'status';
  if (FIX_ACTION_KIND_SET.has(kind)) return 'fixes';
  return 'status';
}

/** Who the row is attributed to: their name, else their email, else a neutral fallback. */
export function formatGapActivityActor(user: GapActionUser | null): string {
  if (!user) return 'Unknown';
  return user.name?.trim() || user.email?.trim() || 'Unknown';
}

const MISSING_FIX_TEXT = 'a fix no longer on this gap';

export interface GapActivityFixQuote {
  /** 1-based, matching `numberedFixes` — `null` when the fix is no longer on the gap. */
  position: number | null;
  text: string;
}

/** The fix a `fix_*` action touched, resolved by `fixId` against the gap's current `suggestedFixes`. */
export function resolveGapActivityFixQuote(
  fixId: string | null,
  fixes: readonly GapSuggestedFix[]
): GapActivityFixQuote {
  if (fixId) {
    const match = numberedFixes(fixes).find(({ fix }) => fix.fixId === fixId);
    if (match) return { position: match.position, text: match.fix.text };
  }
  return { position: null, text: MISSING_FIX_TEXT };
}

/**
 * The verb phrase after the actor's name — "acknowledged the gap", "marked fix 2 resolved". A
 * `fix_*` verb names the fix by its resolved position (`resolveGapActivityFixQuote`), falling
 * back to "a fix" for one no longer on the gap so the sentence still reads.
 */
export function formatGapActivityVerb(
  action: Pick<GapAction, 'type' | 'rawType'>,
  fixQuote: GapActivityFixQuote | null
): string {
  const kind = gapActivityActionKind(action);
  const fixLabel = fixQuote?.position !== null && fixQuote?.position !== undefined ? `fix ${fixQuote.position}` : 'a fix';
  switch (kind) {
    case 'ack':
      return 'acknowledged the gap';
    case 'dispute':
      return 'ignored the gap';
    case 'mark_fixed':
      return 'resolved the gap';
    case 'route_tech':
      return 'routed the gap to tech';
    case 'unroute_tech':
      return 'reset the gap to open';
    case 'reopen':
      return 'reopened the gap';
    case 'note':
      return 'added a note';
    case 'fix_resolve':
      return `marked ${fixLabel} resolved`;
    case 'fix_ignore':
      return `ignored ${fixLabel}`;
    case 'fix_route_tech':
      return `routed ${fixLabel} to tech`;
    case 'fix_reopen':
      return `reset ${fixLabel} to open`;
    case 'fix_patch_approve':
      return `approved ${fixLabel}'s data correction`;
    case 'fix_patch_reject':
      return `rejected ${fixLabel}'s data correction`;
    default:
      return humanizeToken(kind).toLowerCase();
  }
}

/** A run's start or cancel, parsed from a system `note` — `null` for every other entry. */
type RunLifecycleNote = { event: 'started'; note: RunStartedNote } | { event: 'cancelled'; note: RunCancelledNote };

function parseRunLifecycleNote(text: string | null): RunLifecycleNote | null {
  const started = parseRunStartedNote(text);
  if (started) return { event: 'started', note: started };
  const cancelled = parseRunCancelledNote(text);
  if (cancelled) return { event: 'cancelled', note: cancelled };
  return null;
}

function formatCount(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** "2 tickets · 7 regression checks", or just the tickets when the run has no checks. */
function formatRunStartedBody(note: RunStartedNote): string {
  const tickets = formatCount(note.tickets, 'ticket', 'tickets');
  if (note.checks === 0) return tickets;
  return `${tickets} · ${formatCount(note.checks, 'regression check', 'regression checks')}`;
}

/** "Stopped after 2 of 9 · 0 passed · 2 failed" — how far it got, then the gap's own tickets. */
function formatRunCancelledBody(note: RunCancelledNote): string {
  return `Stopped after ${note.done} of ${note.total} · ${note.passed} passed · ${note.failed} failed`;
}

/**
 * The system row's title — "Test passed", "Test failed", a run's start or cancel, or "Test run
 * recorded" for a verdict-note.
 */
function formatGapActivitySystemTitle(
  kind: string,
  isVerification: boolean,
  lifecycle: RunLifecycleNote | null
): string {
  if (kind === 'pass_revoked') return 'Pass revoked';
  if (kind === 'test_passed') return 'Test passed';
  if (kind === 'test_failed') return 'Test failed';
  if (lifecycle) return lifecycle.event === 'started' ? 'Test run started' : 'Test run cancelled';
  if (isVerification) return 'Test run recorded';
  return humanizeToken(kind);
}

/**
 * The system row's meta line — only a revoked pass needs one ("Triggered by <name>'s Resolve");
 * a test run's starter already shows as the row's actor, so it gets none.
 */
function formatGapActivitySystemMeta(kind: string, actorName: string): string | null {
  if (kind === 'pass_revoked') return `Triggered by ${actorName}'s Resolve`;
  return null;
}

/** The timeline icon tile's colour family — one per kind of event, per the approved timeline design. */
export type GapActivityTone =
  | 'note'
  | 'run'
  | 'run_passed'
  | 'run_failed'
  | 'routed'
  | 'resolved'
  | 'ignored'
  | 'status'
  | 'opened';

const ACTION_KIND_TONE: Readonly<Record<string, GapActivityTone>> = {
  note: 'note',
  route_tech: 'routed',
  fix_route_tech: 'routed',
  mark_fixed: 'resolved',
  fix_resolve: 'resolved',
  fix_patch_approve: 'resolved',
  dispute: 'ignored',
  fix_ignore: 'ignored',
  fix_patch_reject: 'ignored',
  test_passed: 'run_passed',
  test_failed: 'run_failed',
};

/** A recorded run passed when nothing failed and at least one ticket passed. */
function isPassingRunCounts(counts: VerificationRunNoteCounts): boolean {
  return counts.failed === 0 && counts.passed > 0;
}

/**
 * The tone for an action. A verification run's `note` is coloured by its own counts; every other
 * kind maps through `ACTION_KIND_TONE`, and anything unmapped (ack, reopen, pass_revoked, the
 * reset kinds, a future kind) reads as a plain status move.
 */
export function gapActivityTone(kind: string, runCounts: VerificationRunNoteCounts | null): GapActivityTone {
  if (kind === 'note' && runCounts) return isPassingRunCounts(runCounts) ? 'run_passed' : 'run_failed';
  return ACTION_KIND_TONE[kind] ?? 'status';
}

/**
 * The system row's body — parsed counts for a test run, what a started or cancelled run covered,
 * the fixed pass-revoked copy, or the raw text.
 */
function formatGapActivitySystemBody(
  kind: string,
  text: string | null,
  verification: VerificationRunNoteCounts | null,
  lifecycle: RunLifecycleNote | null
): string {
  if (kind === 'pass_revoked') {
    return 'A fix target changed after the passing run. Re-run the tests before resolving.';
  }
  if (verification) {
    return `${verification.passed} passed · ${verification.failed} failed · ${verification.skipped} skipped`;
  }
  if (lifecycle) {
    return lifecycle.event === 'started' ? formatRunStartedBody(lifecycle.note) : formatRunCancelledBody(lifecycle.note);
  }
  return text ?? '';
}

interface GapActivityEntryCommon {
  id: string;
  at: string;
  isOptimistic: boolean;
  /** The gap's status right after this entry, when the action moved it. */
  resultingStatus: GapStatus | null;
  tone: GapActivityTone;
  /** Who the row is attributed to — shown beside its initial on the card's top-right. */
  actorName: string;
}

export interface GapActivityPersonEntry extends GapActivityEntryCommon {
  kind: 'person';
  filterGroup: GapActivityFilterGroup;
  verb: string;
  fixQuote: GapActivityFixQuote | null;
  /** The note's own text, in full, for a person's `note` — `null` on every other kind. */
  noteText: string | null;
}

export interface GapActivitySystemEntry extends GapActivityEntryCommon {
  kind: 'system';
  filterGroup: GapActivityFilterGroup;
  title: string;
  meta: string | null;
  body: string;
  /** A test run's parsed counts, for the pass/fail bar — `null` on a revoked pass. */
  runCounts: VerificationRunNoteCounts | null;
}

export interface GapActivityGapOpenedEntry extends GapActivityEntryCommon {
  kind: 'gap_opened';
}

export type GapActivityEntry = GapActivityPersonEntry | GapActivitySystemEntry | GapActivityGapOpenedEntry;

function buildGapActivityPersonEntry(action: GapAction, fixes: readonly GapSuggestedFix[]): GapActivityPersonEntry {
  const kind = gapActivityActionKind(action);
  const fixQuote = FIX_ACTION_KIND_SET.has(kind) ? resolveGapActivityFixQuote(action.fixId, fixes) : null;
  return {
    id: action.id,
    at: action.createdAt,
    kind: 'person',
    filterGroup: gapActivityFilterGroup(action),
    isOptimistic: action.isOptimistic,
    resultingStatus: gapActivityResultingStatus(action),
    tone: gapActivityTone(kind, null),
    actorName: formatGapActivityActor(action.user),
    verb: formatGapActivityVerb(action, fixQuote),
    fixQuote,
    noteText: kind === 'note' ? action.text : null,
  };
}

function buildGapActivitySystemEntry(action: GapAction): GapActivitySystemEntry {
  const kind = gapActivityActionKind(action);
  const verification = parseVerificationRunNote(action.text);
  const lifecycle = parseRunLifecycleNote(action.text);
  const actorName = formatGapActivityActor(action.user);
  return {
    id: action.id,
    at: action.createdAt,
    kind: 'system',
    filterGroup: gapActivityFilterGroup(action),
    isOptimistic: action.isOptimistic,
    resultingStatus: gapActivityResultingStatus(action),
    // A start or cancel has no verdict to colour, so it reads as a neutral test-run entry.
    tone: lifecycle ? 'run' : gapActivityTone(kind, verification),
    actorName,
    title: formatGapActivitySystemTitle(kind, verification !== null, lifecycle),
    meta: formatGapActivitySystemMeta(kind, actorName),
    body: formatGapActivitySystemBody(kind, action.text, verification, lifecycle),
    runCounts: verification,
  };
}

/** One `gap_actions` row, rendered as whichever Activity-tab entry it actually is. */
export function buildGapActivityEntry(
  action: GapAction,
  fixes: readonly GapSuggestedFix[]
): GapActivityPersonEntry | GapActivitySystemEntry {
  return isSystemGapActivityAction(action)
    ? buildGapActivitySystemEntry(action)
    : buildGapActivityPersonEntry(action, fixes);
}

/** The "Gap opened" entry's actor — the investigator that raised the gap. */
export const GAP_OPENED_ACTOR_NAME = 'Investigator';

/**
 * The full Activity timeline: every action newest-first, plus a final "Gap opened" entry from the
 * gap's own `createdAt`. That entry is always last — appended after the sort, not folded into it —
 * so a clock-skewed action timestamp can never push it out of place.
 */
export function buildGapActivityTimeline(
  gap: Pick<GapDetail, 'actions' | 'createdAt' | 'suggestedFixes'>
): GapActivityEntry[] {
  const entries: GapActivityEntry[] = gap.actions
    .map((action) => buildGapActivityEntry(action, gap.suggestedFixes))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  entries.push({
    id: 'gap-opened',
    at: gap.createdAt,
    kind: 'gap_opened',
    isOptimistic: false,
    resultingStatus: null,
    tone: 'opened',
    actorName: GAP_OPENED_ACTOR_NAME,
  });

  return entries;
}

export type GapActivityFilter = 'all' | GapActivityFilterGroup;

/** The filter chips, in the approved order — "All" always first. */
export const GAP_ACTIVITY_FILTERS: readonly { key: GapActivityFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'status', label: 'Status' },
  { key: 'fixes', label: 'Fixes' },
  { key: 'test_runs', label: 'Test runs' },
  { key: 'notes', label: 'Notes' },
];

/** "Gap opened" is All-only — every other chip is a `filterGroup` match. */
export function filterGapActivityEntries(
  entries: readonly GapActivityEntry[],
  filter: GapActivityFilter
): GapActivityEntry[] {
  if (filter === 'all') return [...entries];
  return entries.filter((entry) => entry.kind !== 'gap_opened' && entry.filterGroup === filter);
}

const EMPTY_GAP_ACTIVITY_MESSAGES: Record<GapActivityFilterGroup, string> = {
  status: 'No status changes yet.',
  fixes: 'No fix actions yet.',
  test_runs: 'No test runs yet.',
  notes: 'No notes yet.',
};

/** The one-line message a filtered-empty timeline shows instead of a blank pane. */
export function gapActivityEmptyMessage(filter: GapActivityFilter): string {
  if (filter === 'all') return 'No activity yet.';
  return EMPTY_GAP_ACTIVITY_MESSAGES[filter];
}
